/**
 * MonthRangePicker
 *
 * Selector de período con 3 modos:
 *   1) "mes": navega por meses con flechas ◀ ▶
 *   2) "rango": rango de fechas custom (desde/hasta)
 *   3) "periodo": un período contable de la empresa (S2-02), mensual o de
 *      4 semanas; las flechas van al anterior y al siguiente. Solo aparece si
 *      la pantalla pasa `periodos`.
 *
 * Al click en el label central abre un popup con quick filters + rango custom.
 *
 * Props:
 *   - value: { modo: 'mes'|'rango'|'periodo', periodo?: 'YYYY-MM', periodoId?, nombre?, desde?: 'YYYY-MM-DD', hasta?: 'YYYY-MM-DD' }
 *   - periodos: (opcional) los períodos contables, de `usePeriodosContables`
 *   - onChange: (newValue) => void
 *   - onRefresh: () => void
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, RefreshCw, Calendar, Check, Layers } from 'lucide-react';
import { ordenarPeriodos, valorDePeriodo, periodoVecino, periodoQueCubre, hoyCR } from '../utils/periodosContables';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const fmtMes = (p) => {
  if (!p) return '';
  const [y, m] = p.split('-').map(Number);
  return `${MESES[m - 1]} ${y}`;
};

const fmtFecha = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-').map(Number);
  return `${String(day).padStart(2, '0')} ${MESES[m - 1]} ${y}`;
};

// Helpers
const mesActual = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
};
const ultimoDiaMes = (year, month) => new Date(year, month, 0).getDate();
const rangoDelMes = (periodo) => {
  const [y, m] = periodo.split('-').map(Number);
  const last = ultimoDiaMes(y, m);
  return {
    desde: `${y}-${String(m).padStart(2, '0')}-01`,
    hasta: `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`
  };
};

export default function MonthRangePicker({ value, onChange, onRefresh, onlyMonth = false, periodos = null }) {
  const [open, setOpen] = useState(false);
  const popupRef = useRef(null);
  const containerRef = useRef(null);
  const labelBtnRef = useRef(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const [customDesde, setCustomDesde] = useState(value?.desde || '');
  const [customHasta, setCustomHasta] = useState(value?.hasta || '');

  // Posicionar popup respecto al label (vía Portal → no se corta por overflow del padre)
  useLayoutEffect(() => {
    if (!open || !labelBtnRef.current) return;
    const r = labelBtnRef.current.getBoundingClientRect();
    const popupWidth = 320;
    let left = r.left + r.width / 2 - popupWidth / 2;
    if (left < 8) left = 8;
    if (left + popupWidth > window.innerWidth - 8) left = window.innerWidth - popupWidth - 8;
    setPopupPos({ top: r.bottom + 8, left });
  }, [open]);

  // Cerrar al click afuera — considerar tanto el container como el popup (que vive en Portal)
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      if (popupRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const modo = value?.modo || 'mes';
  const periodo = value?.periodo || mesActual();

  // Label central según modo
  const labelCentral = modo === 'periodo'
    ? value.nombre
    : modo === 'rango'
      ? (!value.desde && !value.hasta
          ? 'Todo el tiempo'
          : `${fmtFecha(value.desde)} → ${fmtFecha(value.hasta)}`)
      : fmtMes(periodo);

  const conPeriodos = Array.isArray(periodos) && periodos.length > 0;
  const elegirPeriodo = (p) => { if (p) { onChange(valorDePeriodo(p)); setOpen(false); } };
  const vecino = (dir) => (modo === 'periodo' && conPeriodos ? periodoVecino(periodos, value.periodoId, dir) : null);
  const flechaActiva = (dir) => modo === 'mes' || Boolean(vecino(dir));

  // Flechas — en modo mes cambian de mes; en modo período, de período
  const cambiarMes = (dir) => {
    if (modo === 'periodo') { const p = vecino(dir); if (p) onChange(valorDePeriodo(p)); return; }
    if (modo !== 'mes') return;
    const [y, m] = periodo.split('-').map(Number);
    let nm = m + dir, ny = y;
    if (nm > 12) { nm = 1; ny++; }
    else if (nm < 1) { nm = 12; ny--; }
    const np = `${ny}-${String(nm).padStart(2, '0')}`;
    onChange({ modo: 'mes', periodo: np, ...rangoDelMes(np) });
  };

  // Volver a modo "mes" con el mes actual (recupera las flechas ◀ ▶ navegando)
  const setMesActual = () => {
    const p = mesActual();
    onChange({ modo: 'mes', periodo: p, ...rangoDelMes(p) });
    setOpen(false);
  };

  // Quick filters
  const setUltimosMeses = (n) => {
    const hoy = new Date();
    const desde = new Date(hoy.getFullYear(), hoy.getMonth() - (n - 1), 1);
    const dY = desde.getFullYear();
    const dM = String(desde.getMonth() + 1).padStart(2, '0');
    const hY = hoy.getFullYear();
    const hM = String(hoy.getMonth() + 1).padStart(2, '0');
    const hD = String(ultimoDiaMes(hY, hoy.getMonth() + 1)).padStart(2, '0');
    onChange({
      modo: 'rango',
      desde: `${dY}-${dM}-01`,
      hasta: `${hY}-${hM}-${hD}`
    });
    setOpen(false);
  };
  const setTodoElTiempo = () => {
    // Sin filtro de fechas → desde/hasta vacíos → backend no filtra
    onChange({ modo: 'rango', desde: '', hasta: '' });
    setOpen(false);
  };
  const setAnio = (year) => {
    onChange({
      modo: 'rango',
      desde: `${year}-01-01`,
      hasta: `${year}-12-31`
    });
    setOpen(false);
  };
  const aplicarCustom = () => {
    if (!customDesde || !customHasta) return;
    if (customDesde > customHasta) return;
    onChange({ modo: 'rango', desde: customDesde, hasta: customHasta });
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="flex items-center gap-1 relative">
      {/* Mes anterior */}
      <button
        onClick={() => cambiarMes(-1)}
        disabled={!flechaActiva(-1)}
        className="p-2 hover:bg-indigo-50 hover:border-indigo-300 rounded-lg transition-colors border border-gray-300 disabled:opacity-30 disabled:cursor-not-allowed group"
        title={modo === 'periodo' ? 'Período anterior' : modo === 'mes' ? 'Mes anterior' : 'Disponible solo en modo mes'}
      >
        <ChevronLeft className="h-5 w-5 text-indigo-500 group-hover:text-indigo-700 group-disabled:text-gray-400" />
      </button>

      {/* Label central — click para abrir popup */}
      <button
        ref={labelBtnRef}
        onClick={() => setOpen(o => !o)}
        className={`px-3 py-2 bg-white border rounded-lg text-center cursor-pointer transition-all whitespace-nowrap ${
          open ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-300 hover:border-gray-400'
        }`}
        title="Click para más opciones"
        style={{ minWidth: modo === 'rango' ? 170 : 95 }}
        data-prueba="selector-periodo-label"
      >
        <span className="text-sm font-semibold text-gray-900">{labelCentral}</span>
      </button>

      {/* Mes siguiente */}
      <button
        onClick={() => cambiarMes(1)}
        disabled={!flechaActiva(1)}
        className="p-2 hover:bg-indigo-50 hover:border-indigo-300 rounded-lg transition-colors border border-gray-300 disabled:opacity-30 disabled:cursor-not-allowed group"
        title={modo === 'periodo' ? 'Período siguiente' : modo === 'mes' ? 'Mes siguiente' : 'Disponible solo en modo mes'}
      >
        <ChevronRight className="h-5 w-5 text-indigo-500 group-hover:text-indigo-700 group-disabled:text-gray-400" />
      </button>

      {/* Refresh */}
      {onRefresh && (
        <button
          onClick={onRefresh}
          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 ml-1"
          title="Actualizar"
        >
          <RefreshCw className="h-5 w-5 text-gray-600" />
        </button>
      )}

      {/* Popup — vía Portal al body, sin recortar por overflow del padre */}
      {open && createPortal(
        <div
          ref={popupRef}
          onMouseDown={(e) => e.stopPropagation()}
          style={{ position: 'fixed', top: popupPos.top, left: popupPos.left, width: 320, zIndex: 9999 }}
          className="bg-white border border-gray-200 rounded-xl shadow-xl"
        >
          <div className={onlyMonth ? "p-3" : "p-3 border-b border-gray-100"}>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {onlyMonth ? 'Atajos' : 'Filtros rápidos'}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {/* Mes actual — recupera el modo 'mes' con flechas ◀ ▶ */}
              <QuickBtn
                label="Mes actual"
                onClick={setMesActual}
                highlight={modo === 'mes' && periodo === mesActual()}
              />
              {/* Filtros de rango solo cuando NO es onlyMonth */}
              {!onlyMonth && <QuickBtn label="Hace 3 meses" onClick={() => setUltimosMeses(3)} />}
              {!onlyMonth && <QuickBtn label="Hace 6 meses" onClick={() => setUltimosMeses(6)} />}
              {!onlyMonth && <QuickBtn label={`Año ${new Date().getFullYear()}`} onClick={() => setAnio(new Date().getFullYear())} />}
              {!onlyMonth && <QuickBtn label={`Año ${new Date().getFullYear() - 1}`} onClick={() => setAnio(new Date().getFullYear() - 1)} />}
              {!onlyMonth && <QuickBtn label="Todo el tiempo" onClick={setTodoElTiempo} />}
            </div>
          </div>

          {/* Períodos contables de la empresa (S2-02) — mensuales o de 4 semanas */}
          {conPeriodos && (
            <div className="p-3 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Período contable</span>
              </div>
              <div className="flex gap-1.5">
                <select
                  data-prueba="selector-periodo"
                  value={modo === 'periodo' ? String(value.periodoId) : ''}
                  onChange={(e) => elegirPeriodo(periodos.find(p => String(p.id) === e.target.value))}
                  className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                >
                  <option value="">Elegir un período…</option>
                  {ordenarPeriodos(periodos).reverse().map(p => (
                    <option key={p.id} value={String(p.id)}>{p.nombre}{p.estado !== 'abierto' ? ' (cerrado)' : ''}</option>
                  ))}
                </select>
                <QuickBtn label="Actual" onClick={() => elegirPeriodo(periodoQueCubre(periodos, hoyCR()))} />
              </div>
            </div>
          )}

          {/* Sección Rango Personalizado — solo si NO es onlyMonth */}
          {!onlyMonth && (
            <div className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Rango personalizado</span>
              </div>
              <div className="space-y-2">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-0.5">Desde:</label>
                  <input
                    type="date"
                    value={customDesde}
                    onChange={(e) => setCustomDesde(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-0.5">Hasta:</label>
                  <input
                    type="date"
                    value={customHasta}
                    onChange={(e) => setCustomHasta(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                  />
                </div>
                <button
                  onClick={aplicarCustom}
                  disabled={!customDesde || !customHasta || customDesde > customHasta}
                  className="w-full flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Check className="h-3.5 w-3.5" />
                  Aplicar rango
                </button>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

function QuickBtn({ label, onClick, highlight }) {
  return (
    <button
      onClick={onClick}
      className={
        highlight
          ? 'px-2 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded text-left'
          : 'px-2 py-1.5 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors text-left'
      }
    >
      {label}
    </button>
  );
}
